/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Icon, Typography } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';

export interface GroupProps {
  heading: string;
  description: RichTextHTMLString;
  links: {
    text: string;
    url: string;
    showRating?: boolean;
  }[];
}

const linkFocusStyles = css({
  content: '""',
  position: 'absolute',
  border: `2px solid ${Colors.neutral800}`,
  borderRadius: '3px',
  inset: '-2px -10px',
});

const Styles = labelStyles('Group', {
  root: css({
    padding: '40px 20px',
    backgroundColor: Colors.neutral100,
    border: `1px solid ${Colors.neutral800}`,
    borderRadius: '5px',
    boxShadow: `6px 6px 0 ${Colors.neutral800}`,

    [MEDIA_MIN_WIDTH.small]: {
      padding: '40px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      padding: '44px',
    },
  }),

  heading: css({
    ...Typography.pageHeader7,
    color: Colors.earth,
    marginBottom: '8px',
  }),

  description: css({
    ...Typography.heading,
    fontSize: '16px',
    lineHeight: '22px',
    color: Colors.neutral800,
    marginBottom: '32px',

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.subHeader3,
      minHeight: '84px',
      marginBottom: '48px',
    },
  }),

  list: css({
    listStyle: 'none',
  }),

  listItem: css({
    borderBottom: `1px solid ${Colors.neutral800}`,
  }),

  link: (showRating?: boolean) => [
    css({
      ...Typography.subHeader5Medium,
      fontSize: '16px',
      lineHeight: '24px',
      color: Colors.neutral800,
      textDecoration: 'none',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      padding: '12px 48px 12px 0',
      outline: 'none',
      transition: 'color 250ms',

      [MEDIA_MIN_WIDTH.large]: {
        ...Typography.subHeader3Medium,
        padding: '16px 48px 16px 0',
      },

      '&:hover': {
        color: '#7B7977',
      },

      '&:focus::before': linkFocusStyles,
      '&:focus-visible::before': linkFocusStyles,
      '&:focus:not(:focus-visible)::before': { display: 'none' },
    }),
    showRating &&
      withModifier(
        'hasRating',
        css({
          paddingRight: '144px',
        }),
      ),
  ],

  arrow: css({
    height: '24px',
    position: 'absolute',
    right: 0,
    transition: 'all 250ms',

    'a:hover &': {
      transform: 'translateX(4px)',
    },
  }),

  rating: css({
    height: '24px',
    position: 'absolute',
    right: 0,
    backgroundColor: Colors.neutral100,
    transition: 'all 250ms',

    'a:hover &, a:focus &': {
      opacity: 0,
      transform: 'translateX(4px)',
    },
  }),
});

export function Group(props: GroupProps) {
  return (
    <div css={Styles.root}>
      <h3 css={Styles.heading}>{props.heading}</h3>
      <div css={Styles.description}>
        <RichTextHtml html={props.description} />
      </div>
      <ul css={Styles.list}>
        {props.links.map((link) => (
          <li css={Styles.listItem} key={link.text}>
            <a css={Styles.link(link.showRating)} href={link.url}>
              <span>{link.text}</span>
              <span css={Styles.arrow}>
                <Icon
                  color={COLOR_KEYS.zapier}
                  name="arrowBigRight"
                  size={24}
                />
              </span>
              {link.showRating && (
                <span css={Styles.rating}>
                  {[...Array(5)].map((_, index) => (
                    <Icon
                      color={COLOR_KEYS.zapier}
                      key={index}
                      name="ratingStarFill"
                      size={24}
                    />
                  ))}
                </span>
              )}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
